import { useDispatch, useSelector } from "react-redux";
import { AgentCommissionRule } from "../../../models/AgentCommissionRule";
import { User } from "../../../models/User";
import { Modal } from "../../Modal";
import { AppDispatch, RootState } from "../../../state/store";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Toast } from "../../Toast";
import { SubmitHandler, useForm } from "react-hook-form";
import { ReactiveFormInput } from "../../form/ReactiveFormInput";
import { ReactiveFormSelect } from "../../form/ReactiveFormSelect";
import { RoleEnum } from "../../../enums/Role";
import Button from "../../Button";
import { createAgencyUserCommissionRule } from "../../../state/agencies/action";
import { CommissionRuleType } from "../../../models/CommissionRuleType";
import { toast, ToastContainer } from "react-toastify";
import { PlusIcon } from "@heroicons/react/24/outline";
import { LoadingMask } from "../../LoadingMask";

interface Props {
  info: {
    show: boolean;
    user?: User;
    currentUser?: User;
    commissionRuleTypes: CommissionRuleType[] | [];
  };
  onSubmited: (rules: AgentCommissionRule[]) => void;
  onClose: () => void;
}

export const UserRuleModal: React.FC<Props> = ({
  info,
  onClose,
  onSubmited,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<any>(null);
    const [showError, setShowError] = useState(false);

    const [selectedCommissionRuleType, setSelectedCommissionRuleType] = useState<CommissionRuleType | undefined>();
    const [selectedAgents, setSelectedAgents] = useState<{ agent: User; value: number }[]>([]);
    const [selectedAgentsEmptyError, setSelectedAgentsEmptyError] = useState(false);

    const agents = useSelector((state: any) => state.users.users);
    
    let sumOfValues = () => selectedAgents.reduce((a, b) => a + b.value, 0);
    let currentAgentValue = () => +(createRuleMethods.getValues("value") ?? 0);

    const createRuleMethods = useForm<any>({
        defaultValues: {},
    });
    const { handleSubmit: handleCreateRuleSubmit, control: createRuleControl } = createRuleMethods;

    useEffect(() => {
        if (showError) {
          Toast(t(error?.reason ?? "SOMETHING_WENT_WRONG"), t(error?.cause_info));
          setShowError(false);
        }
    }, [showError, error, t]);

    const closeModal = () => {
        setSelectedAgents([]);
        setSelectedCommissionRuleType(undefined);
        createRuleMethods.reset();
        setSelectedAgentsEmptyError(false);
        onClose()
    }

    const onCreateRuleSubmit: SubmitHandler<AgentCommissionRule> = async (
        data: AgentCommissionRule
      ) => {
        setIsLoading(true);

        if (data.carrier_id) {
          data.carrier_id = +data.carrier_id;
        }
    
        if (data.value) {
          data.value = selectedCommissionRuleType?.is_relative
            ? +data.value / 100
            : +data.value;
        }
    
        if (data.commission_rule_type_id) {
          data.commission_rule_type_id = +data.commission_rule_type_id;
        }
    
        if (selectedCommissionRuleType?.id === 4) {
          if (selectedAgents.length === 0) {
            return setSelectedAgentsEmptyError(true);
          }
        }
    
        let rules: { user_id: number; data: AgentCommissionRule }[] = [];
    
        if (selectedCommissionRuleType?.id === 4) {
          rules = [
            {
              user_id: info.user!.id!,
              data: {
                name: data.name,
                commission_rule_type_id: selectedCommissionRuleType.id,
                value: data.value,
              },
            },
            ...selectedAgents.map((x) => {
              return {
                user_id: x.agent.id!,
                data: {
                  commission_rule_type_id: selectedCommissionRuleType?.id,
                  value: x.value,
                  name: `${data.name} (${x.agent.first_name})`,
                },
              };
            }),
          ];
        } else {
          rules = [
            {
              user_id: info.user!.id!,
              data: {
                name: data.name,
                commission_rule_type_id: selectedCommissionRuleType?.id,
                value: data.value,
              },
            },
          ];
        }

        dispatch(
          createAgencyUserCommissionRule({
            agencyId: info.currentUser!.agency_id!,
            userId: info.user!.id!,
            payload: rules,
          })
        ).then((e) => {
          if (e.type === "agencies/createAgencyUserCommissionRule/rejected") {
            setIsLoading(false);
            setError(e.payload);
            setShowError(true);
          } else {
            setIsLoading(false); 
            toast(t("SAVED_SUCCESFULLY"));
            let rules = (e.payload || []) as AgentCommissionRule[]
            onSubmited(rules);
            closeModal();
          }
        });
    };

    return(<>
        <Modal
            isOpen={info.show}
            onClose={closeModal}
            label={t("NEW_COMMISSION_RULE")}
            onSave={handleCreateRuleSubmit(onCreateRuleSubmit)}
        >
          <div className="flex flex-col space-y-3">
              <div className="w-full flex items-start">
                  <ReactiveFormInput
                      className="md:w-1/2"
                      control={createRuleControl}
                      label={t("NAME")}
                      name="name"
                      isRequired/>

                  <div className="md:w-1/2">
                      <ReactiveFormSelect
                          control={createRuleControl}
                          label={t("COMMISSION_RULE_TYPE")}
                          name="comission_rule_type_id"
                          options={info.commissionRuleTypes.map((el) => {
                              return { value: `${el.id}`, label: `${el.name}` };
                          })}
                          handleOnChange={(e) => {
                              createRuleMethods.setValue("value", "");
                              setSelectedCommissionRuleType(info.commissionRuleTypes.find((el) => el.id == e.target.value));
                          }}
                          isRequired/>

                      <p className="text-slate-500 text-xs px-3 block">
                          {`${selectedCommissionRuleType?.description ?? ""}`}
                      </p>
                  </div>
                  {/* 
                      <ReactiveFormSelect
                          control={createRuleControl}
                          className='md:w-1/2'
                          label={t("CARRIER")}
                          name="carrier_id"
                          options={getCarriers().filter(x => commissionRules.find(y => y.carrier_id === x.key) === undefined)}
                          isRequired /> */}
              </div>
  
              <div className="w-full flex items-center">
                  <ReactiveFormInput
                      control={createRuleControl}
                      className="md:w-1/4"
                      label={t("VALUE")}
                      placeholder={
                          selectedCommissionRuleType?.is_relative === false ? "$" : "%"
                      }
                      name="value"
                      type="number"
                      min={selectedCommissionRuleType?.id !== 3 ? 0 : undefined}
                      max={
                          selectedCommissionRuleType?.id === 4
                              ? 100 - sumOfValues()
                              : selectedCommissionRuleType?.id === 1
                              ? 100
                              : selectedCommissionRuleType?.id == 3
                              ? -1
                              : undefined
                      }
                      isRequired
                  />
              </div>
  
              {selectedCommissionRuleType?.id === 4 && (
                  <div className="w-full flex flex-col space-y-1">
                      <p className="font-medium text-md p-3">{t("OTHER_AGENTS")}</p>
                      <div className="w-full divide-y divide-dashed">
                          {selectedAgents.map((x) => (
                              <div className="flex p-3">
                              <p className="md:w-1/2">
                                  {x.agent.first_name} {x.agent.last_name}
                              </p>
                              <p className="md:w-1/2 font-semibold">{x.value}%</p>
                              </div>
                          ))}
          
                          <div className="py-3 w-full flex items-start">
                              <div className="md:w-1/2">
                              <ReactiveFormSelect
                                  label={t("AGENT")}
                                  name="selectedOtherAgent"
                                  control={createRuleControl}
                                  options={agents
                                  .filter(
                                      (x: User) =>
                                      x.role_id === RoleEnum.AGENT &&
                                      x.id !== info.user!.id! &&
                                      selectedAgents.find((y) => y.agent.id == x.id) ===
                                          undefined
                                  )
                                  .map((x: User) => {
                                      return {
                                      label: `${x.first_name} ${x.last_name}`,
                                      value: `${x.id}`,
                                      };
                                  })}
                              />
                              {selectedAgentsEmptyError && (
                                  <p className="text-red-500 text-xs italic ml-3">
                                  {t("NO_OTHER_AGENTS_SELECTED")}
                                  </p>
                              )}
                              </div>
          
                              <ReactiveFormInput
                                  control={createRuleControl}
                                  className="md:w-1/4"
                                  label={t("VALUE")}
                                  placeholder={"%"}
                                  name="selectedOtherAgentValue"
                                  min={1}
                                  max={100 - (sumOfValues() + currentAgentValue())}
                                  type="number"
                              />
          
                              <Button
                                  classNames="mt-8"
                                  onClick={async () => {
                                      const result = await createRuleMethods.trigger([
                                      "selectedOtherAgent",
                                      "selectedOtherAgentValue",
                                      ]);
              
                                      if (!result) {
                                      return;
                                      }
              
                                      let selectedUser = agents.find(
                                      (x: User) =>
                                          x.id ==
                                          createRuleMethods.getValues("selectedOtherAgent")
                                      );
                                      let value = +createRuleMethods.getValues(
                                      "selectedOtherAgentValue"
                                      );
              
                                      if (!selectedUser) {
                                      createRuleMethods.setError("selectedOtherAgent", {
                                          message: t("FIELD_REQUIRED"),
                                      });
                                      return;
                                      }
              
                                      if (isNaN(value)) {
                                      createRuleMethods.setError("selectedOtherAgentValue", {
                                          message: t("FIELD_REQUIRED"),
                                      });
                                      return;
                                      }
              
                                      createRuleMethods.resetField("selectedOtherAgent");
                                      createRuleMethods.resetField("selectedOtherAgentValue", {
                                      defaultValue: "",
                                      });
              
                                      setSelectedAgentsEmptyError(false);
              
                                      setSelectedAgents((prev) => {
                                      let copy = [...prev];
                                      copy.push({ agent: selectedUser, value: value });
              
                                      return copy;
                                      });
                                  }}
                                  >
                                  <span className="flex items-center font-semibold pr-3">
                                      <div className="w-8 p-1 aspect-square mr-2">
                                          <PlusIcon />
                                      </div>
                                      {t("ADD")}
                                  </span>
                              </Button>
                          </div>
                      </div>
                  </div>
              )}
          </div>
        </Modal>
        <ToastContainer progressStyle={{ background: "#D4AF37" }} />
        { isLoading && <LoadingMask />}
    </>)
}